import dayjs from 'dayjs'

import 'common/services/dateService'
import {
  draftFromRun,
  runDateRange,
} from 'features/Schedule/pages/components/ManageAppointment/EhrAppointmentV2/Series/edit/seriesEdit.model'
import { SERIES_TEXT_V2 } from 'features/Schedule/pages/components/ManageAppointment/EhrAppointmentV2/Series/seriesText'

import type { AppointmentSeriesLink, SeriesRun, SeriesRunVisit } from '../api/series.types'
import { MAX_SERIES_VISITS } from '../series.constants'

export type SeriesTick = 'done' | 'current' | 'todo'

export type SeriesPopoverDetails = {
  step: number
  range: string | null
  session: string | null
  ticks: SeriesTick[]
}

// A Syncfusion redraw remounts card templates; a run fetched this recently is reused.
export const RUN_REFETCH_SECONDS = 30

const CLOCK = /^\d{2}:\d{2}/
const ENDS_FORMAT = 'ddd, MMM D'
const CLOCK_FORMAT = 'h:mm'

// Site wall-clock digits as written; no zone is involved.
const clockAt = (time: string, plusMinutes: number): string =>
  dayjs
    .utc(`2000-01-01T${time.slice(0, 5)}:00`)
    .add(plusMinutes, 'minute')
    .format(CLOCK_FORMAT)

const visitClock = (visit: SeriesRunVisit | undefined): { start: string; end: string } | null => {
  if (!visit?.time || !CLOCK.test(visit.time)) return null
  return { start: clockAt(visit.time, 0), end: clockAt(visit.time, visit.duration) }
}

const rangeLine = (cadence: string | null, visit: SeriesRunVisit | undefined): string | null => {
  const clock = visitClock(visit)
  if (!clock) return cadence
  if (!cadence) return `${clock.start}–${clock.end}`
  return SERIES_TEXT_V2.popoverRange(cadence, clock.start, clock.end)
}

// One tick per planned visit; a step the run has no row for is still to do.
// A corrupt total never builds more ticks than a Series can hold.
const tickCount = (total: number): number =>
  Math.min(MAX_SERIES_VISITS, Math.max(0, Math.trunc(total) || 0))

const tickFor = (visit: SeriesRunVisit | undefined, step: number, current: number): SeriesTick => {
  if (visit?.bucket === 'completed') return 'done'
  return step === current ? 'current' : 'todo'
}

export const seriesPopoverDetails = (
  run: SeriesRun,
  link: AppointmentSeriesLink,
  appointmentId: number,
): SeriesPopoverDetails => {
  const matched = run.visits.find((item) => item.appointmentId === appointmentId)
  const step = matched?.step ?? link.step
  const visit = matched ?? run.visits.find((item) => item.step === step)
  const { last } = runDateRange(run, draftFromRun(run))
  return {
    step,
    range: rangeLine(run.cadenceLabel, visit),
    session: last
      ? SERIES_TEXT_V2.popoverSession(step, run.total, dayjs.utc(last).format(ENDS_FORMAT))
      : null,
    ticks: Array.from({ length: tickCount(run.total) }, (_, index) =>
      tickFor(
        run.visits.find((item) => item.step === index + 1),
        index + 1,
        step,
      ),
    ),
  }
}
